import type { ChangeEvent } from 'react';
import { useState, useCallback } from 'react';
import { v4 as generateUUID } from 'uuid';
import styled from '@emotion/styled';
import { tokens, TextInput } from '@hotjar/hotsauce';
import type {
  FilterKindEnum,
  FilterType,
  SingleFilterState,
  UpsellFooterEnum,
  UpsellType,
} from '../../../domains/global-filters/model/FilterChain';
import { FilterCategoryEnum } from '../../../domains/global-filters/model/FilterChain';
import { FilterCategory } from './AddFilterCategory';
import { AddFilterFooter } from './AddFilterFooter';

export const DROPDOWN_WIDTH = 469;

export const ONE_COLUMN_DROPDOWN_WIDTH = 258;

const Container = styled.div<{ hasOneColumn: boolean }>`
  width: ${({ hasOneColumn }) => (hasOneColumn ? ONE_COLUMN_DROPDOWN_WIDTH : DROPDOWN_WIDTH)}px;
`;

const TextInputContainer = styled.div`
  padding: ${tokens.spacer.x2} ${tokens.spacer.x2} 0 ${tokens.spacer.x2};
`;

const NotFoundDiv = styled.div`
  ${tokens.flexCenter()}
  padding: ${tokens.spacer.x3} 0 ${tokens.spacer.x3} 0;
`;

const FilterGrid = styled.div<{ hasOneColumn: boolean }>`
  columns: ${({ hasOneColumn }) => (hasOneColumn ? 1 : 2)};
  padding: ${tokens.spacer.x3} ${tokens.spacer.x2} 0 ${tokens.spacer.x2};
  gap: 0 ${tokens.spacer.x1};
`;

function initFilterStateByCategory(
  filterStateByKind: Map<FilterKindEnum, SingleFilterState>,
  searchString: string,
): Map<FilterCategoryEnum, SingleFilterState[]> {
  const filterToShowList = searchString
    ? Array.from(filterStateByKind.values()).filter((item) =>
        item.metaData.filterName.toLowerCase().includes(searchString),
      )
    : Array.from(filterStateByKind.values());

  const filterToShowByCategory = new Map<FilterCategoryEnum, SingleFilterState[]>();
  filterToShowList.forEach((item) => {
    const { categoryName } = item.metaData;
    const filterList = filterToShowByCategory.get(categoryName) ?? [];
    filterList.push(item);
    filterToShowByCategory.set(item.metaData.categoryName, filterList);
  });

  return filterToShowByCategory;
}

interface AddFilterDropdownProps {
  filterStateByKind: Map<FilterKindEnum, SingleFilterState>;
  onNewFilter: (newSingleFilter: FilterType) => void;
  isMaxReached: boolean;
  hasOneColumn: boolean;
  showUpsellFooter?: UpsellFooterEnum;
  onUpsellClicked?: (upsellType: UpsellType) => void;
  isFunnelStep?: boolean;
}

export const AddFilterDropdown = ({
  filterStateByKind,
  onNewFilter,
  isMaxReached,
  hasOneColumn,
  showUpsellFooter,
  onUpsellClicked,
  isFunnelStep,
}: AddFilterDropdownProps): JSX.Element => {
  const [filterStateByCategory, setFilterStateByCategory] = useState<
    Map<FilterCategoryEnum, SingleFilterState[]>
  >(initFilterStateByCategory(filterStateByKind, ''));

  const handleNewSearchString = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const searchString = event.target.value?.toLowerCase().trim();
      const newfilterStateByCategory = initFilterStateByCategory(filterStateByKind, searchString);
      setFilterStateByCategory(newfilterStateByCategory);
    },
    [filterStateByKind, setFilterStateByCategory],
  );

  const handleNewFilter = useCallback(
    (newKind: FilterKindEnum) => {
      // Currently Unions have limitation on the number of members they can have.
      // This is why, at the moment, we need to cast it.
      // https://github.com/microsoft/TypeScript/issues/47077
      onNewFilter({
        id: generateUUID(),
        kind: newKind,
      } as FilterType);
    },
    [onNewFilter],
  );

  return (
    <Container hasOneColumn={hasOneColumn}>
      {isFunnelStep !== true && (
        <TextInputContainer>
          <TextInput
            id="searchInput"
            name="Search filters"
            icon="search"
            placeholder="Search filters…"
            size="m"
            defaultValue=""
            onChange={handleNewSearchString}
            autoComplete="off"
            autoFocus
          />
        </TextInputContainer>
      )}
      {filterStateByCategory.size === 0 && <NotFoundDiv>No filters found.</NotFoundDiv>}
      {filterStateByCategory.size !== 0 && (
        <FilterGrid hasOneColumn={hasOneColumn}>
          <FilterCategory
            name={FilterCategoryEnum.Path}
            filterStateList={filterStateByCategory.get(FilterCategoryEnum.Path)}
            onNewFilter={handleNewFilter}
            onUpsellClicked={onUpsellClicked}
            isMaxReached={isMaxReached}
          />
          <FilterCategory
            name={FilterCategoryEnum.Session}
            filterStateList={filterStateByCategory.get(FilterCategoryEnum.Session)}
            onNewFilter={handleNewFilter}
            onUpsellClicked={onUpsellClicked}
            isMaxReached={isMaxReached}
          />
          <FilterCategory
            name={FilterCategoryEnum.Behavior}
            filterStateList={filterStateByCategory.get(FilterCategoryEnum.Behavior)}
            onNewFilter={handleNewFilter}
            onUpsellClicked={onUpsellClicked}
            isMaxReached={isMaxReached}
          />
          <FilterCategory
            name={FilterCategoryEnum.UserAttributes}
            filterStateList={filterStateByCategory.get(FilterCategoryEnum.UserAttributes)}
            onNewFilter={handleNewFilter}
            onUpsellClicked={onUpsellClicked}
            isMaxReached={isMaxReached}
          />
          <FilterCategory
            name={FilterCategoryEnum.Technology}
            filterStateList={filterStateByCategory.get(FilterCategoryEnum.Technology)}
            onNewFilter={handleNewFilter}
            onUpsellClicked={onUpsellClicked}
            isMaxReached={isMaxReached}
          />
          <FilterCategory
            name={FilterCategoryEnum.Feedback}
            filterStateList={filterStateByCategory.get(FilterCategoryEnum.Feedback)}
            onNewFilter={handleNewFilter}
            onUpsellClicked={onUpsellClicked}
            isMaxReached={isMaxReached}
          />
          <FilterCategory
            name={FilterCategoryEnum.Experiment}
            filterStateList={filterStateByCategory.get(FilterCategoryEnum.Experiment)}
            onNewFilter={handleNewFilter}
            onUpsellClicked={onUpsellClicked}
            isMaxReached={isMaxReached}
          />
        </FilterGrid>
      )}
      <AddFilterFooter showUpsellFooter={showUpsellFooter} onUpsellClicked={onUpsellClicked} />
    </Container>
  );
};
